import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { candidatesAPI, interviewsAPI } from '../api/client';
import '../pages/AnimationsAndStyles.css';

export default function Dashboard() {
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : { name: 'User', role: 'HR' };

  const [stats, setStats] = useState({
    totalCandidates: 0,
    interviewsScheduled: 0,
    offersExtended: 0
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [candidatesRes, interviewsRes] = await Promise.all([
        candidatesAPI.getAll(),
        interviewsAPI.getAll()
      ]);

      setStats({
        totalCandidates: candidatesRes.data.length,
        interviewsScheduled: interviewsRes.data.filter(i => i.status === 'scheduled').length,
        offersExtended: interviewsRes.data.filter(i => i.status === 'interview').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{css}</style>
      
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📊 Dashboard</h1>
          <p style={styles.subtitle}>
            Welcome back, <strong>{user.name}</strong>
          </p>
        </div>
        <div style={styles.roleBadge}>{user.role}</div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={styles.loader}>
          <div style={styles.spinner}></div>
          <p>Loading dashboard data...</p>
        </div>
      ) : (
        <div style={styles.grid}>
          <StatCard
            title="Total Candidates"
            value={stats.totalCandidates}
            color="#2563EB"
            icon="👥"
          />
          <StatCard
            title="Interviews Scheduled"
            value={stats.interviewsScheduled}
            color="#0EA5E9"
            icon="📅"
          />
          <StatCard
            title="In Interview Stage"
            value={stats.offersExtended}
            color="#1E40AF"
            icon="🎯"
          />
        </div>
      )}
    </div>
  );
}

/* ================= STAT CARD COMPONENT ================= */
function StatCard({ title, value, color, icon }) {
  return (
    <div style={{ ...styles.card, borderLeft: `6px solid ${color}` }}>
      <div style={styles.cardIcon}>{icon}</div>
      <h2 style={styles.cardValue}>{value}</h2>
      <p style={styles.cardTitle}>{title}</p>
    </div>
  );
}

/* ================= INLINE STYLES ================= */
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0F172A, #1E3A8A)',
    padding: '30px',
    fontFamily: "'Inter', sans-serif",
    color: '#FFFFFF',
    position: 'relative',
    animation: 'fadeInUp 0.6s ease-out',
  },

  header: {
    background: '#FFFFFF',
    color: '#0F172A',
    borderRadius: '16px',
    padding: '28px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    boxShadow: '0 15px 35px rgba(0,0,0,0.15)',
    animation: 'fadeInDown 0.5s ease-out',
  },

  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    fontFamily: "'Poppins', sans-serif",
  },

  subtitle: {
    marginTop: '8px',
    color: '#64748B',
    fontSize: '14px',
    fontWeight: 400,
  },

  roleBadge: {
    background: 'linear-gradient(135deg, #1E40AF, #1e3a8a)',
    color: '#FFFFFF',
    padding: '10px 18px',
    borderRadius: '22px',
    fontSize: '13px',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
    animation: 'slideInRight 0.5s ease-out',
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '28px',
    animation: 'fadeInUp 0.6s ease-out 0.1s both',
  },

  card: {
    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
    color: '#0F172A',
    borderRadius: '18px',
    padding: '32px 28px',
    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default',
    border: '1px solid rgba(226, 232, 240, 0.6)',
    position: 'relative',
    overflow: 'hidden',
  },

  cardIcon: {
    fontSize: '32px',
    marginBottom: '12px',
    animation: 'float 3s ease-in-out infinite',
  },

  cardValue: {
    fontSize: '42px',
    fontWeight: 800,
    margin: '0 0 8px 0',
    fontFamily: "'Poppins', sans-serif",
    color: '#1E40AF',
  },

  cardTitle: {
    fontSize: '14px',
    color: '#64748B',
    margin: 0,
    fontWeight: 500,
  },

  loader: {
    textAlign: 'center',
    padding: '80px 20px',
    fontSize: '16px',
    color: '#E5E7EB',
    animation: 'fadeInUp 0.5s ease-out',
  },

  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(226, 232, 240, 0.3)',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite',
  },
};

const css = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-12px);
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// import React, { useEffect, useState } from 'react';
// import { jwtDecode } from 'jwt-decode';
// import { candidatesAPI, interviewsAPI, offersAPI } from '../api/client';

// export default function Dashboard() {
//   const user = jwtDecode(localStorage.getItem('token'));

//   const [stats, setStats] = useState({
//     totalCandidates: 0,
//     interviewsScheduled: 0,
//     offersExtended: 0
//   });
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     fetchStats();
//   }, []);

//   const fetchStats = async () => {
//     try {
//       const [candidatesRes, interviewsRes] = await Promise.all([
//         candidatesAPI.getAll(),
//         interviewsAPI.getAll()
//       ]);

//       setStats({
//         totalCandidates: candidatesRes.data.length,
//         interviewsScheduled: interviewsRes.data.filter(i => i.status === 'scheduled').length,
//         offersExtended: interviewsRes.data.filter(i => i.status === 'interview').length
//       });
//     } catch (error) {
//       console.error('Error fetching stats:', error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="card">
//       <h1>Welcome {user.name}</h1>
//       <p className="muted">Role: {user.role}</p>

//       {loading ? (
//         <p className="loading">Loading...</p>
//       ) : (
//         <div className="grid">
//           <div className="stat-card">
//             <h3>{stats.totalCandidates}</h3>
//             <p>Total Candidates</p>
//           </div>
//           <div className="stat-card">
//             <h3>{stats.interviewsScheduled}</h3>
//             <p>Interviews Scheduled</p>
//           </div>
//           <div className="stat-card">
//             <h3>{stats.offersExtended}</h3>
//             <p>In Interview Stage</p>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// // import React, { useEffect, useState } from 'react';
// // import { candidatesAPI, interviewsAPI, offersAPI } from '../api/client';

// // export default function Dashboard() {
// //   const [stats, setStats] = useState({
// //     totalCandidates: 0,
// //     interviewsScheduled: 0,
// //     offersExtended: 0
// //   });
// //   const [loading, setLoading] = useState(true);

// //   useEffect(() => {
// //     fetchStats();
// //   }, []);

// //   const fetchStats = async () => {
// //     try {
// //       const [candidatesRes, interviewsRes, offersRes] = await Promise.all([
// //         candidatesAPI.getAll(),
// //         interviewsAPI.getAll(),
// //         offersAPI.getById(1).catch(() => ({ data: [] }))
// //       ]);

// //       setStats({
// //         totalCandidates: candidatesRes.data.length,
// //         interviewsScheduled: interviewsRes.data.filter(i => i.status === 'scheduled').length,
// //         offersExtended: interviewsRes.data.filter(i => i.status === 'interview').length
// //       });
// //     } catch (error) {
// //       console.error('Error fetching stats:', error);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   return (
// //     <div className="card">
// //       <h1>Dashboard</h1>
// //       {loading ? (
// //         <p className="loading">Loading...</p>
// //       ) : (
// //         <div className="grid">
// //           <div className="stat-card">
// //             <h3>{stats.totalCandidates}</h3>
// //             <p>Total Candidates</p>
// //           </div>
// //           <div className="stat-card">
// //             <h3>{stats.interviewsScheduled}</h3>
// //             <p>Interviews Scheduled</p>
// //           </div>
// //           <div className="stat-card">
// //             <h3>{stats.offersExtended}</h3>
// //             <p>In Interview Stage</p>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }
